/**
 * ANA SAYFA — defter açıkken (10.10.2026; kararlar
 * `claude/notlar/2026-10-10-dashboard-ve-lisans-taslaklari.md`).
 *
 * Tanıtım sayfasındaki kartlar burada defterin İÇERİĞİNE göre özetine döner.
 * Eşikler her açılışta cihazdaki gerçek veriden okunur, ayrıca saklanmaz ve
 * sunucuya bir şey sorulmaz:
 *  - Gizlilik ve Profiller (en üstte): kilit kapısı varsa açık profil ve profil sayısı
 *  - Hesaplar ve hareketler: ≥1 hesap VE ≥1 hareket
 *  - Rapor / Analiz: her zaman tanıtım + giriş noktası
 *  - Yedek ve eşitleme: bir yedek hedefi kurulu
 *  - Otomatik fiyatlar (EN SONDA, kullanıcı 12.10.2026; ızgarada da yeri sabit, 28.10.2026): ≥1 fiyatı izlenen araç.
 *
 * Sayılar raporun kendi kartıyla gösterilir (dayanak, bayatlık, yokluk sebebi
 * tek kopya). Ana sayfa kendi uyarısını üretmez: anormallikler bantlarda.
 */

import { DepolamaSorusu } from "../components/DepolamaSorusu";
import { HucreIzgarasi } from "../components/HucreIzgarasi";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import type { AssetBinding, IsoDate } from "@parafon/contracts";
import type { GetiriOzeti, GetiriOzetiAlani } from "@parafon/contracts/local-api";
import { KARTLAR, Kart } from "../components/tanitim/kartlar";
import { kayitliRaporParaBirimi } from "../lib/raporParaBirimi";
import { routeHref, type Route } from "../lib/router";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useAsync } from "../repo/useAsync";
import { HEDEF_ADI } from "../yedek/tipler";
import { useYedek } from "../yedek/YedekSaglayici";
import { errorText } from "../lib/rpcError";
import { HesapAdi } from "../ui/HesapAdi";
import { ErrorBlock, Screen } from "../ui/primitives";
import { formatRatio } from "../lib/decimal";
import { gunEkle, seriYukle } from "../lib/seriYukle";
import { kapsamBellegi } from "../lib/hesapKapsami";
import { kuralOzeti, sanalVeriEsitle } from "../lib/sanalHesap";
import { useCentral } from "../central/CentralContext";
import { formatMoney } from "../lib/decimal";
import type { SanalHesapTanimi, ValuationSeriesPoint } from "@parafon/contracts/local-api";
import { bugunYerelIso } from "../lib/tarihDonemleri";
import { M, YEREL } from "../dil";
import { formatIsoDate } from "../lib/format";
import { motorMetniVeya } from "../dil/motorMetni";

function Git({
  route,
  children,
  birincil = false,
}: {
  readonly route: Route;
  readonly children: ReactNode;
  readonly birincil?: boolean;
}) {
  return (
    <a
      href={routeHref(route)}
      className={birincil ? "pf-btn pf-btn-primary" : "pf-btn pf-btn-ghost"}
    >
      {children}
    </a>
  );
}

/** "Son 1 yıl" penceresi — motorla aynı sayı (`getiriOzeti.ts`). */
const SON_YIL_GUN = 365;

const GETIRI_SATIRLARI: readonly { readonly alan: GetiriOzetiAlani; readonly ad: string }[] = [
  { alan: "toplam", ad: M.anaSayfa.getiri.toplam },
  { alan: "sonYil", ad: M.anaSayfa.getiri.sonYil },
  { alan: "gunluk", ad: M.anaSayfa.getiri.gunluk },
];

function GetiriSatirlari({
  getiri,
}: {
  readonly getiri:
    | { readonly durum: "bos" }
    | { readonly durum: "yukleniyor"; readonly biten: number; readonly toplam: number }
    | { readonly durum: "tamam"; readonly ozet: GetiriOzeti; readonly para: string }
    | { readonly durum: "hata"; readonly hata: unknown };
}) {
  if (getiri.durum === "bos") return null;
  if (getiri.durum === "hata") {
    return <p className="text-[12px] text-muted">{M.anaSayfa.getiri.hesaplanamadi(errorText(getiri.hata))}</p>;
  }
  if (getiri.durum === "yukleniyor") {
    return (
      <p className="text-[12px] text-muted" role="status">
        {getiri.toplam > 0 ? M.anaSayfa.getiri.hesaplaniyorGun(getiri.biten, getiri.toplam) : M.anaSayfa.getiri.hesaplaniyor}
      </p>
    );
  }
  const { ozet, para } = getiri;
  return (
    <dl aria-label={M.anaSayfa.getiri.etiket(para)} className="space-y-1">
      {GETIRI_SATIRLARI.map(({ alan, ad }) => {
        const v = ozet[alan];
        const yok = ozet.absent[alan];
        return (
          <div key={alan} className="flex items-baseline justify-between gap-3">
            <dt className="text-muted">{ad}</dt>
            <dd className="text-right">
              {v !== null ? (
                <span className="pf-num text-[15px] font-semibold text-ink">{formatRatio(v)}</span>
              ) : (
                <span className={yok?.kind === "yok" ? "text-faint" : "text-warn"} title={motorMetniVeya(yok?.reason, M.anaSayfa.getiri.sebepBilinmiyor)}>
                  — <span className="text-[11px]">{motorMetniVeya(yok?.reason, M.anaSayfa.getiri.sebepBilinmiyor)}</span>
                </span>
              )}
            </dd>
          </div>
        );
      })}
      <p className="text-[11px] text-faint">{M.anaSayfa.getiri.dipnot(para)}</p>
    </dl>
  );
}

function gunAdi(iso: IsoDate): string {
  return formatIsoDate(iso);
}

function zaman(iso: string): string {
  return new Date(iso).toLocaleString(YEREL, {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function varlikAnahtari(a: AssetBinding): string {
  return a.kind === "tracked"
    ? `t:${a.instrumentId}`
    : `m:${a.label}:${a.currency}`;
}

export function AnaSayfaScreen() {
  const { repo, revision } = useRepositoryContext();
  const yedek = useYedek();

  const defter = useAsync(
    useCallback(async () => {
      const [hesaplar, hareketler, pozisyonlar, profiller] = await Promise.all([
        repo.accounts.list(),
        repo.transactions.query({ limit: 1 }),
        repo.positions(),
        repo.kilit?.durum() ?? Promise.resolve(null),
      ]);
      // Açık profilin hesapları ve her birinin hareket sayısı (kullanıcı,
      // 14.10.2026). Hesap başına bir sayım sorgusu; satırlar okunmuyor.
      const hesapSayilari = await Promise.all(
        hesaplar.map(async (h) => ({
          id: h.id,
          ad: h.name,
          renk: h.renk,
          hareket: (await repo.transactions.query({ accountIds: [h.id], limit: 1 })).total,
        })),
      );
      const izlenen = new Set(
        pozisyonlar
          .filter((p) => p.asset.kind === "tracked")
          .map((p) => varlikAnahtari(p.asset)),
      );
      const elle = new Set(
        pozisyonlar
          .filter((p) => p.asset.kind === "manual")
          .map((p) => varlikAnahtari(p.asset)),
      );
      return {
        hesap: hesaplar.length,
        hesapSayilari,
        hareket: hareketler.total,
        sonHareket: hareketler.rows[0]?.tradeDate ?? null,
        izlenen: izlenen.size,
        elle: elle.size,
        acikProfil: profiller?.profil ?? null,
        profiller: profiller?.profiller ?? null,
      };
    }, [repo]),
    [repo, revision],
  );
  const d = defter.state.data;
  const kayitVar = d !== undefined && d.hesap > 0 && d.hareket > 0;

  // GETİRİ ÖZETİ (kullanıcı kararı 13.10.2026): piyasa değeri yerine toplam,
  // son 1 yıl ve günlük getiri. Seri Günlük Analiz'in önbelleğinden
  // (`seriYukle`); ilk seferde bir yıl hesaplanır, sonra hazırdır. Yalnızca
  // kayıt varken (boş defterde söyleyecek bir şey yok).
  const [getiri, setGetiri] = useState<
    | { readonly durum: "bos" }
    | { readonly durum: "yukleniyor"; readonly biten: number; readonly toplam: number }
    | { readonly durum: "tamam"; readonly ozet: GetiriOzeti; readonly para: string }
    | { readonly durum: "hata"; readonly hata: unknown }
  >({ durum: "bos" });
  useEffect(() => {
    if (!kayitVar) {
      setGetiri({ durum: "bos" });
      return undefined;
    }
    let iptal = false;
    const para = kayitliRaporParaBirimi();
    const bugun = bugunYerelIso();
    setGetiri((g) => (g.durum === "tamam" ? g : { durum: "yukleniyor", biten: 0, toplam: 0 }));
    void (async () => {
      try {
        const noktalar = await seriYukle(repo, para, gunEkle(bugun, -SON_YIL_GUN), bugun, {
          bugun,
          iptal: () => iptal,
          ilerleme: (biten, toplam) => {
            if (!iptal) setGetiri((g) => (g.durum === "tamam" ? g : { durum: "yukleniyor", biten, toplam }));
          },
        });
        if (iptal || noktalar === null) return;
        if (repo.getiriOzeti === undefined) throw new Error(M.kabuk.depo.getiriOzetiYok);
        const ozet = await repo.getiriOzeti(noktalar);
        if (!iptal) setGetiri({ durum: "tamam", ozet, para });
      } catch (hata) {
        if (!iptal) setGetiri({ durum: "hata", hata });
      }
    })();
    return () => {
      iptal = true;
    };
  }, [repo, kayitVar, revision]);

  const yd = yedek?.durum;
  const yedekKurulu = yd?.kurulu === true;
  // Defterde kayıt var ama yedek yok: anormallik. Uyarıyı sayfanın üstündeki
  // yedek bandı veriyor (tek anormallik, tek işaret); kart yalnızca "Yedeği
  // kur"u öne çıkarır.
  const yedeksizKayit = yd?.kurulu === false && yedek?.defterBos === false;

  const hesapVar = d !== undefined && d.hesap > 0;
  const profilKarti = (
    <Kart
      key="profil"
      {...KARTLAR.defter}
      {...(d?.acikProfil !== null && d?.acikProfil !== undefined
        ? {
            // Hangi profil açık, kaç profil var; açık profilin hesapları ve
            // hareket sayıları (kullanıcı, 14.10.2026). Öbür profiller kilitli:
            // içleri okunamaz, yalnızca sayıları bilinir.
            ozet: (
              <div className="space-y-2">
                <p className="text-muted">
                  {M.anaSayfa.profil.acikProfil}<span className="font-semibold text-ink">{d.acikProfil.etiket}</span>
                  {d.profiller !== null && d.profiller.length > 1 && <>{M.anaSayfa.profil.profilSayisi(d.profiller.length)}</>}
                </p>
                {d.hesapSayilari.length === 0 ? (
                  <p className="text-[12px] text-faint">{M.anaSayfa.profil.hesapYok}</p>
                ) : (
                  // Kart yana genişleyince hesaplar sütunlara yayılır (28.10.2026).
                  <ul aria-label={M.anaSayfa.profil.hesaplari} className="gap-x-6 text-[13px] [columns:13rem]">
                    {d.hesapSayilari.map((h) => (
                      <li key={h.id} className="flex break-inside-avoid items-baseline justify-between gap-3 py-px">
                        <HesapAdi renk={h.renk} className="min-w-0 [overflow-wrap:anywhere]">
                          {h.ad}
                        </HesapAdi>
                        <span className="shrink-0 text-muted">
                          <span className="pf-num text-ink">{h.hareket}</span>
                          {M.anaSayfa.profil.hareketSonEk(h.hareket)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ),
          }
        : {})}
      eylem={<Git route="ayarlar">{M.anaSayfa.profil.ayarlar}</Git>}
    />
  );
  const hesapKarti = (
    <Kart
      key="hesap"
      {...KARTLAR.baslangic}
      {...(kayitVar && d !== undefined
        ? {
            ozet: (
              <div className="space-y-3">
                <p className="text-muted">
                  {M.anaSayfa.hesap.ozet(d.hesap, d.hareket)}
                  {d.sonHareket !== null && (
                    <>{M.anaSayfa.hesap.sonHareket(gunAdi(d.sonHareket))}</>
                  )}
                </p>
                <GetiriSatirlari getiri={getiri} />
              </div>
            ),
            eylem: (
              <>
                <Git route="hareketler">{M.anaSayfa.hesap.hareketler}</Git>
                <Git route="ice-aktar">{M.anaSayfa.hesap.iceAktar}</Git>
              </>
            ),
          }
        : {
            eylem: (
              <>
                <Git route="hesaplar" birincil>
                  {M.anaSayfa.hesap.hesapAc}
                </Git>
                <Git route="hareketler">{M.anaSayfa.hesap.hareketGir}</Git>
                <Git route="ice-aktar">{M.anaSayfa.hesap.dosyadan}</Git>
              </>
            ),
          })}
    />
  );

  return (
    <Screen
      title={M.anaSayfa.ekran.baslik}
      description={M.anaSayfa.ekran.aciklama}
    >
      {/* İlk kurulumdan sonra, cevaplanana kadar (19.10.2026). */}
      <DepolamaSorusu />
      {/* Okunamadıysa kartlar tanıtım gibi görünmemeli: "kayıt yok" ≠ "okunamadı". */}
      {defter.state.status === "error" ? (
        <div className="w-full p-4">
          <ErrorBlock
            error={defter.state.error}
            onRetry={defter.reload}
            title={M.anaSayfa.ekran.okunamadi}
          />
        </div>
      ) : (
        // KART DÜZENİ (Gelen kararlar 28.10.2026): 48 px hücreli ızgara, kart
        // içeriğine göre boyutlanır, sınırlı büyür (`HucreIzgarasi`). Eşit boy
        // (12.10.2026) kalktı. Sıra = görünen sıra; telefonda tek sütun.
        <HucreIzgarasi>
          {/* SIRA (kullanıcı, 13.10.2026): hesap yokken profil kartı başta;
              hesap açılınca hesaplar kartı başa geçer. */}
          {/* Anahtarlar şart: sıra değişince React kartın iç durumunu
              (tanıtım açık/kapalı) öbür karta geçirmesin. */}
          {hesapVar ? [hesapKarti, profilKarti] : [profilKarti, hesapKarti]}
          <Kart
            {...KARTLAR.raporlar}
            eylem={<Git route="rapor">{M.anaSayfa.rapor.ac}</Git>}
          />
          <SanalHesaplarKarti />
          <Kart
            {...KARTLAR.yedek}
            {...(yedekKurulu && yd !== undefined
              ? {
                  ozet: (
                    <div className="space-y-1">
                      <p className="text-muted">
                        {yd.hedefTuru === null
                          ? M.anaSayfa.yedek.hedef
                          : HEDEF_ADI[yd.hedefTuru]}{" "}
                        {M.anaSayfa.yedek.sonYedek}{" "}
                        <span className="pf-fig">
                          {yd.son === null ? M.anaSayfa.yedek.henuzYok : zaman(yd.son.zaman)}
                        </span>
                      </p>
                      {yd.esitleme !== null && (
                        <p className="text-muted">
                          {M.anaSayfa.yedek.obur}{" "}
                          <span className="pf-fig">
                            {yd.esitleme.dosya === 0
                              ? M.anaSayfa.yedek.baskaCihazYok
                              : M.anaSayfa.yedek.alindi(yd.esitleme.alinan, zaman(yd.esitleme.zaman))}
                          </span>
                        </p>
                      )}
                    </div>
                  ),
                }
              : {})}
            eylem={
              <>
                <Git route="ayarlar" birincil={yedeksizKayit}>
                  {yedekKurulu ? M.anaSayfa.yedek.ayarlar : M.anaSayfa.yedek.kur}
                </Git>
              </>
            }
          />
          <Kart
            {...KARTLAR.fiyatlar}
            {...(d !== undefined && d.izlenen > 0
              ? {
                  ozet: (
                    <p className="text-muted">
                      {M.anaSayfa.fiyat.izlenen(d.izlenen)}
                      {d.elle > 0 && <>{M.anaSayfa.fiyat.elle(d.elle)}</>}
                    </p>
                  ),
                }
              : {})}
            eylem={<Git route="izleme">{M.anaSayfa.fiyat.izleme}</Git>}
          />
        </HucreIzgarasi>
      )}
    </Screen>
  );
}

/**
 * SANAL HESAPLAR KARTI (back-test kararı K2'(a), 14.10.2026): yalnızca sanal
 * hesap varsa görünür; toplamlara GİRMEZ. Her hesap için bugünkü toplam
 * varlık ve geçmiş dahil getiri oranı — Günlük Analiz'in aynı motor noktası.
 */
function SanalHesaplarKarti() {
  const { repo, revision } = useRepositoryContext();
  const merkez = useCentral();
  const yukle = useCallback(async () => {
    const liste = (await repo.sanalHesaplar?.liste()) ?? [];
    const bugun = bugunYerelIso();
    const satirlar: { tanim: SanalHesapTanimi; nokta: ValuationSeriesPoint | null; hata: string | null }[] = [];
    for (const t of liste) {
      try {
        await sanalVeriEsitle(repo, merkez, t);
        const son = t.bitis != null && t.bitis < bugun ? t.bitis : bugun;
        const s = await repo.valuationSeries({ from: son, to: son, reportCurrency: t.paraBirimi, accountIds: [t.id] });
        satirlar.push({ tanim: t, nokta: s.points[0] ?? null, hata: null });
      } catch (e) {
        satirlar.push({ tanim: t, nokta: null, hata: errorText(e) });
      }
    }
    return satirlar;
    // `merkez` her çizimde yeni olabilir; eşitleme kendi belleğiyle tekrarlanmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repo]);
  const { state } = useAsync(yukle, [repo, revision]);
  const satirlar = state.data ?? [];
  if (satirlar.length === 0) return null;
  return (
    <Kart
      {...KARTLAR.sanal}
      ozet={
        <ul aria-label={M.anaSayfa.sanal.liste} className="gap-x-6 text-[13px] [columns:13rem]">
          {satirlar.map(({ tanim, nokta, hata }) => (
            <li key={tanim.id} className="break-inside-avoid pb-1.5">
              <div className="flex items-baseline justify-between gap-3">
                <a
                  href={routeHref("rapor")}
                  onClick={() => kapsamBellegi.ac(tanim.id)}
                  className="min-w-0 [overflow-wrap:anywhere]"
                >
                  <HesapAdi renk={tanim.renk}>{tanim.ad}</HesapAdi>
                </a>
                {/* Tutar ve oran ayrı iki değer: dar kartta oran alt satıra
                    geçebilir; her biri kendi içinde bölünmez (pf-num). */}
                <span className="flex flex-wrap justify-end gap-x-2 text-right text-ink">
                  <span className="pf-num">
                    {nokta?.value == null ? "—" : `${formatMoney(nokta.value)} ${tanim.paraBirimi}`}
                  </span>
                  {nokta?.totalReturnRatio != null && (
                    <span className="pf-num text-muted">{formatRatio(nokta.totalReturnRatio)}</span>
                  )}
                </span>
              </div>
              <p className={hata === null ? "text-[11px] text-faint" : "text-[11px] text-warn"}>{hata ?? kuralOzeti(tanim)}</p>
            </li>
          ))}
        </ul>
      }
      eylem={<Git route="sanal-hesaplar">{M.anaSayfa.sanal.git}</Git>}
    />
  );
}
